"use client";

import {
  Children,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
} from "react";
import styles from "./Carousel.module.css";

/* Where `perViewMobile` takes over from `perView`, matching the 767px point the
   page stylesheets already stack at. */
const MOBILE_QUERY = "(max-width: 767px)";

/*
  Replaces the Webflow `w-slider` widget. Three places used it, with different
  settings, which is what the props here cover:

    homepage testimonials   perView 1, autoplay 5s, arrows + round dots
    audits recent reports   perView 3 (1 on phones), no autoplay, arrows only
    careers offsite photos  perView 3, autoplay 4s, next arrow only

  The old careers slider was a 136-line takeover that moved Webflow's slides out
  of its mask into a hand-built track and then had to restore all of it on
  unmount. Expressing "three at a time" as a prop removes that entirely.

  Paging wraps: advancing past the last page returns to the first. The original
  cloned its slides to make the wrap seamless in one direction; the visible
  difference is that the wrap animates back across the track rather than
  continuing forward, and it costs none of the clone bookkeeping.
*/

export function Carousel({
  children,
  label,
  perView = 1,
  perViewMobile,
  autoplay = false,
  interval = 5000,
  showArrows = true,
  showDots = true,
  dotVariant = "round",
  arrowVariant = "chevron",
  nextLabel = "Next slide",
  previousLabel = "Previous slide",
  className,
  viewportClassName,
  slideClassName,
  arrowsClassName,
}: {
  children: ReactNode;
  /* Names the carousel for screen readers, e.g. "Customer testimonials". */
  label: string;
  perView?: number;
  /* Slides per page below `MOBILE_QUERY`. Defaults to `perView`, i.e. no change. */
  perViewMobile?: number;
  autoplay?: boolean;
  interval?: number;
  showArrows?: boolean;
  showDots?: boolean;
  dotVariant?: "round" | "number";
  /* "chevron" flanks the slides with bare chevrons; "square" is the pair of
     solid blue tiles the homepage testimonials park below the slide, which the
     caller places with `arrowsClassName`. */
  arrowVariant?: "chevron" | "square";
  nextLabel?: string;
  previousLabel?: string;
  className?: string;
  viewportClassName?: string;
  slideClassName?: string;
  arrowsClassName?: string;
}) {
  const slides = Children.toArray(children);

  /*
    Slide widths come from the custom properties set on the root below, so the
    first paint is already correct on a phone. Only the paging arithmetic needs
    to know in JS which breakpoint is live, and it settles at hydration: the
    server snapshot is the desktop count, because there is no viewport to
    measure until then. Page 0 looks identical either way, so nothing moves.
  */
  const subscribeToBreakpoint = useCallback(
    (onChange: () => void) => {
      if (perViewMobile === undefined) return () => {};
      const query = window.matchMedia(MOBILE_QUERY);
      query.addEventListener("change", onChange);
      return () => query.removeEventListener("change", onChange);
    },
    [perViewMobile],
  );

  const perViewNow = useSyncExternalStore(
    subscribeToBreakpoint,
    () => (perViewMobile !== undefined && window.matchMedia(MOBILE_QUERY).matches ? perViewMobile : perView),
    () => perView,
  );

  const pageCount = Math.max(1, Math.ceil(slides.length / perViewNow));
  const [rawPage, setPage] = useState(0);
  // Clamped on read rather than corrected in an effect: if the slide count ever
  // shrinks, an effect would need an extra render to notice, and would paint one
  // frame scrolled past the end first.
  const page = Math.min(rawPage, pageCount - 1);
  const [paused, setPaused] = useState(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  const pointerStart = useRef<{ x: number; y: number } | null>(null);

  /*
    Autoplay stops while the pointer is over the carousel or focus is inside it,
    and never starts at all when the visitor has asked for reduced motion — the
    old widget's timer ignored both, and also outlived the element it animated.
  */
  useEffect(() => {
    if (!autoplay || paused || pageCount < 2) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = window.setInterval(() => setPage((current) => (current + 1) % pageCount), interval);
    return () => window.clearInterval(timer);
  }, [autoplay, paused, interval, pageCount]);

  const go = (next: number) => setPage(((next % pageCount) + pageCount) % pageCount);

  function onPointerDown(event: React.PointerEvent) {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    pointerStart.current = { x: event.clientX, y: event.clientY };
  }

  function onPointerUp(event: React.PointerEvent) {
    const start = pointerStart.current;
    pointerStart.current = null;
    if (!start) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    // Ignore mostly-vertical drags so a swipe down the page does not page the
    // carousel sideways.
    if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
    go(page + (dx < 0 ? 1 : -1));
  }

  return (
    <section
      className={[styles.root, className].filter(Boolean).join(" ")}
      style={
        {
          "--carousel-per-view": perView,
          "--carousel-per-view-mobile": perViewMobile ?? perView,
        } as CSSProperties
      }
      aria-roledescription="carousel"
      aria-label={label}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setPaused(false);
      }}
    >
      <div
        ref={viewportRef}
        className={[styles.viewport, viewportClassName].filter(Boolean).join(" ")}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={() => {
          pointerStart.current = null;
        }}
      >
        <div
          className={styles.track}
          /* `aria-live` is off while autoplay runs so the reader is not
             interrupted every few seconds; manual paging announces normally. */
          aria-live={autoplay ? "off" : "polite"}
          style={{ transform: `translate3d(${-page * 100}%, 0, 0)` }}
        >
          {slides.map((slide, index) => {
            const slidePage = Math.floor(index / perViewNow);
            return (
              <div
                key={index}
                className={[styles.slide, slideClassName].filter(Boolean).join(" ")}
                role="group"
                aria-roledescription="slide"
                aria-label={`${index + 1} of ${slides.length}`}
                /* Off-screen slides must not be reachable by Tab. */
                inert={slidePage !== page}
              >
                {slide}
              </div>
            );
          })}
        </div>
      </div>

      {pageCount > 1 && showArrows && (
        /* Layout-neutral while the buttons are pinned beside the slides; the row
           that carries them once they drop below, or once they are tiles. */
        <div
          className={[styles.arrows, arrowVariant === "square" && styles.arrowsSquare, arrowsClassName]
            .filter(Boolean)
            .join(" ")}
        >
          <button
            type="button"
            className={[styles.arrow, styles.arrowPrev, arrowVariant === "square" && styles.arrowSquare]
              .filter(Boolean)
              .join(" ")}
            onClick={() => go(page - 1)}
          >
            <span className="sr-only">{previousLabel}</span>
            <span className={styles.chevron} aria-hidden="true" />
          </button>
          <button
            type="button"
            className={[styles.arrow, styles.arrowNext, arrowVariant === "square" && styles.arrowSquare]
              .filter(Boolean)
              .join(" ")}
            onClick={() => go(page + 1)}
          >
            <span className="sr-only">{nextLabel}</span>
            <span className={styles.chevron} aria-hidden="true" />
          </button>
        </div>
      )}

      {pageCount > 1 && showDots && (
        <div className={dotVariant === "number" ? styles.dotsNumber : styles.dots}>
          {Array.from({ length: pageCount }, (_, index) => (
            <button
              key={index}
              type="button"
              className={styles.dot}
              aria-current={index === page}
              onClick={() => go(index)}
            >
              {dotVariant === "number" ? index + 1 : <span className="sr-only">{`Go to slide ${index + 1}`}</span>}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
